// @ts-nocheck
import React, { FC } from 'react';

import InverseButton from 'crehana-ui/Buttons/InverseButton';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import { grayDark } from 'crehana-ui/styles/colors';
import CheckNew from 'crehana-icons/CheckNew';

import { useTranslation } from '@crehana/i18n';

import useCountdown from 'crehana-react-hooks/useCountdown';
import buyProduct, { formatProduct } from 'crehana-utils/buyProduct';

import { Membership, MembershipCardColors } from '../../types';

const CountdownText: React.FC<{
  expirationDate: Date;
  message: string;
}> = ({ expirationDate, message }) => {
  const { rawDays, hours, minutes, finished, hasStarted } =
    useCountdown(expirationDate);

  if (finished || !hasStarted) return null;

  return (
    <p className="tw-font-semibold text-center tw-font-pop text-14">
      <span className="inline-flex text-base-main">{message}</span>
      <span className="inline-block ml-4">
        {`${rawDays}d ${hours}h ${minutes}m`}
      </span>
    </p>
  );
};

interface MembershipCardProps {
  isFirstItem: boolean;
  isLastItem: boolean;
  membership: Membership;
  ribbonText?: string;
  colors: MembershipCardColors;
  isDarkTheme?: boolean;
  promo: {
    message: string;
    alternativeShortMessage: string;
    expirationDate: Date;
  } | null;
  showRegularPrice: boolean;
}

const MembershipCard: FC<MembershipCardProps> = ({
  isFirstItem,
  isLastItem,
  membership: {
    titleI18nKey,
    tag,
    prices,
    urlCheckout,
    licenseNumber,
    benefits,
    buttonLabel,
    isHighlighted,
    promoMessage,
  },
  membership,
  colors,
  isDarkTheme = true,
  promo,
  showRegularPrice = false,
}) => {
  const { t } = useTranslation();

  console.log('prices', prices);

  const handleBuyProduct = () => {
    try {
      if (window.gtag) {
        window.gtag('event', 'Abre Checkout', {
          send_to: 'UA-60829200-1',
          event_category: 'Membresias',
          event_label: membership.tag,
          value: membership.prices.total,
        });
      }

    } catch (error) {
      console.error(error);
    }

    const product = formatProduct(membership, 'membership');

    buyProduct(urlCheckout, product);
  };

  const getMonthlyPrice = () => {
    if (prices?.monthlyPrice) {
      if (prices.currencySymbol === 'CO$') {
        return parseInt(String(prices.monthlyPrice), 10).toLocaleString();
      }

      return prices.monthlyPrice.toLocaleString();
    }
    return '';
  };

  const yearlyPrice = `${
    prices.currencySymbol + prices.total.toLocaleString()
  }`;

  const Price = (
    <div className="flex flex-wrap items-center">
      <p
        className="mr-8 font-body1"
        style={{ color: isHighlighted ? colors.accent : colors.text }}
      >
        {prices.currencySymbol}
      </p>
      <div>
        <p
          className="flex items-center font-main-title sm:hidden"
          style={{ color: isHighlighted ? colors.accent : colors.text }}
        >
          {getMonthlyPrice()}
          <span
            className="ml-8 font-body1"
            style={{ color: isHighlighted ? colors.accent : colors.text }}
          >
            / mes*
          </span>
        </p>
        {/* Desktop */}
        <p
          className="items-center hidden font-h2 sm:flex"
          style={{ color: isHighlighted ? colors.accent : colors.text }}
        >
          {getMonthlyPrice()}
          <span
            className="ml-8 font-body1"
            style={{ color: isHighlighted ? colors.accent : colors.text }}
          >
            / mes*
          </span>
        </p>
      </div>
    </div>
  );

  const renderLicencesBenefit = () => {
    if (tag === 'PREMIUM_DUO')
      return (
        <li className="flex items-center mb-12">
          <CheckNew size={24} className="mr-8" color={colors.listIcon} />
          <p className="font-body3" style={{ color: colors.text }}>
            {t('TOTAL_ACCOUNTS_DUO')}
          </p>
        </li>
      );
    if (tag === 'FAMILIAR')
      return (
        <li className="flex items-center mb-12">
          <CheckNew size={24} className="mr-8" color={colors.listIcon} />
          <p className="font-body3" style={{ color: colors.text }}>
            {t('TOTAL_ACCOUNTS_FAMILY')}
          </p>
        </li>
      );
  };

  const title = t(titleI18nKey);

  return (
    <article
      className={`mb-24 md:mb-0 ${isHighlighted ? 'md:-mt-24' : ''} ${
        isLastItem ? 'md:ml-auto lg:ml-0' : ''
      } ${
        isFirstItem ? 'md:mr-auto lg:mr-0' : ''
      } bg-base-main rounded-5 overflow-hidden`}
    >
      {/* Highlighted header message */}
      {isHighlighted && (
        <React.Fragment>
          {promo ? (
            <div
              className="flex items-center justify-center h-32"
              style={{
                marginBottom: -2,
                backgroundColor: colors.accent,
              }}
            >
              <CountdownText
                expirationDate={promo.expirationDate}
                message={
                  promoMessage
                    ? `${promoMessage}: ${t('ENDS_IN', {
                        defaultValue: 'Termina en ',
                      })}`
                    : t('OFFER_ENDS_IN', {
                        defaultValue: 'Precio especial: termina en ',
                      })
                }
              />
            </div>
          ) : (
            <div
              style={{
                paddingTop: 8,
                paddingBottom: 2,
                backgroundColor: colors.accent,
              }}
            >
              <p className="text-center text-black font-label">
                {promoMessage || t('POPULAR')}
              </p>
            </div>
          )}
        </React.Fragment>
      )}
      <div
        className="px-24 py-32 sm:px-24 md:px-20 lg:px-40"
        style={{
          border: isHighlighted
            ? `2px solid ${colors.accent}`
            : `2px solid transparent`,
        }}
      >
        <div>
          <header className="mb-24">
            {/* Title */}
            <p
              className="mb-12 text-left font-h2-md"
              style={{
                color: isHighlighted ? colors.accent : colors.text,
              }}
            >
              {title}
            </p>
            {showRegularPrice && prices.realPrice && (
              <p className="mb-4 font-body4 text-yellow-main">
                Antes:{' '}
                <span className="line-through">
                  {prices.currencySymbol + prices.realPrice.toLocaleString()}
                </span>
              </p>
            )}
            {/* Monthly Price */}
            <div className="flex items-center mb-16">{Price}</div>
            {/* Yearly price */}
            <p
              className="text-left font-subtitle3"
              style={{
                maxWidth: 240,
                color: grayDark,
              }}
            >
              {t('MEMBERSHIP_SECTION_YEARLY', {
                interpolation: { yearlyPrice },
                defaultValue: '* Cobro anual de {{yearlyPrice}}',
              })}
            </p>
            {/* Max licenses */}
            <p
              className="mt-4 text-left font-subtitle3"
              style={{ color: colors.text }}
            >
              {licenseNumber
                ? t('MEMBERSHIP_SECTION_MAX_MEMBERS', {
                    interpolation: {
                      licences: licenseNumber - 1,
                      addedPlural: licenseNumber > 2 ? 's' : '',
                    },
                    defaultValue:
                      'Compártelo con {{licences}} persona{{addPlural}}',
                  })
                : 'Cuenta individual'}
            </p>
          </header>
          <ul className="w-full -mb-12">
            {benefits.map(el => (
              <li key={el.id} className="flex items-center mb-12">
                <CheckNew size={24} className="mr-8" color={colors.listIcon} />
                <p className="font-body3" style={{ color: colors.text }}>
                  {el.text}
                </p>
              </li>
            ))}
            {tag !== 'PREMIUM' ? (
              renderLicencesBenefit()
            ) : (
              <div className="w-full h-24 mb-12" />
            )}
          </ul>
          <div className="mt-32">
            {isHighlighted ? (
              <PrimaryButton
                size="l"
                full
                darkMode={isDarkTheme}
                className="px-40 md:px-20 lg:px-40"
                label={
                  buttonLabel ||
                  t('MEMBERSHIPS_SECTION_BUTTON_3', {
                    defaultValue: 'Empezar ahora',
                  })
                }
                onClick={handleBuyProduct}
              />
            ) : (
              <InverseButton
                size="l"
                full
                white={isDarkTheme}
                className="px-40 md:px-20 lg:px-40"
                label={
                  buttonLabel ||
                  t('MEMBERSHIPS_SECTION_BUTTON_3', {
                    defaultValue: 'Empezar ahora',
                  })
                }
                onClick={handleBuyProduct}
              />
            )}
          </div>
          <p
            className="mt-24 text-left sm:text-center lg:text-left font-legals"
            style={{ letterSpacing: 0, color: colors.legals }}
          >
            {t('MEMBERSHIPS_SECTION_NOTE')}
          </p>
        </div>
      </div>
    </article>
  );
};

export default MembershipCard;
